import type { ApiClient } from "@riea-water/api-client";
import { ApiProblem, unwrap } from "@riea-water/api-client";
import type { components } from "@riea-water/contracts";
import {
  authConfigSchema,
  connectionSchema,
  connectionSyncSchema,
  evidenceSchema,
  fieldJobPageSchema,
  fieldJobSchema,
  findingDetailSchema,
  findingSchema,
  importSchema,
  ruleRunSchema,
  ruleSchema,
  meSchema,
  meterReadSchema,
  modulesResponseSchema,
  quarantinedRowSchema,
  supplyPointDetailSchema,
  supplyPointSummarySchema,
  timelineEntrySchema,
  type AuthConfig,
  type Connection,
  type ConnectionSync,
  type Evidence,
  type FieldJob,
  type FieldJobPage,
  type FieldJobResult,
  type Finding,
  type FindingDetail,
  type Import,
  type Me,
  type Rule,
  type RuleRun,
  type ModulesResponse,
  type QuarantinedRow,
  type SupplyPointDetail,
  type TimelineEntry,
} from "@riea-water/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

export const queryKeys = {
  authConfig: ["auth-config"] as const,
  me: ["me"] as const,
  modules: ["modules"] as const,
  imports: ["imports"] as const,
  quarantine: (importId: string) => ["imports", importId, "quarantine"] as const,
  connections: ["connections"] as const,
  syncs: (connectionId: string) => ["connections", connectionId, "syncs"] as const,
  supplyPoints: (filters: Record<string, unknown>) => ["supply-points", filters] as const,
  supplyPoint: (spid: string) => ["supply-points", spid] as const,
  timeline: (spid: string) => ["supply-points", spid, "events"] as const,
  reads: (meterId: string) => ["meters", meterId, "reads"] as const,
  findings: (filters: Record<string, unknown>) => ["findings", filters] as const,
  finding: (findingId: string) => ["findings", "one", findingId] as const,
  rules: ["rules"] as const,
  ruleRuns: ["rule-runs"] as const,
};

/** Unauthenticated: what the sign-in screen shows before a session exists (D-26). Validated at the edge. */
export async function fetchAuthConfig(origin = window.location.origin): Promise<AuthConfig> {
  const response = await fetch(`${origin}/api/v1/auth/config`, {
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw new Error(`auth config: ${response.status}`);
  return authConfigSchema.parse(await response.json());
}

export function useMe(client: ApiClient, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: async (): Promise<Me> => meSchema.parse(unwrap(await client.GET("/me"))),
    enabled,
    retry: (count, error) =>
      !(error instanceof ApiProblem && error.problem.status === 403) && count < 2,
    staleTime: 60_000,
  });
}

export function useModules(client: ApiClient, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.modules,
    queryFn: async (): Promise<ModulesResponse> =>
      modulesResponseSchema.parse(unwrap(await client.GET("/modules"))),
    enabled,
    staleTime: 60_000,
  });
}

const importsPage = z.object({ items: z.array(importSchema), nextCursor: z.string().nullable() });
const quarantinePage = z.object({
  items: z.array(quarantinedRowSchema),
  nextCursor: z.string().nullable(),
});
const connectionsResponse = z.object({
  items: z.array(connectionSchema),
  notConnected: z.array(z.object({ name: z.string(), reason: z.string() })),
});

/** The imports list, newest first; refreshed while any import is still validating or committing. */
export function useImports(client: ApiClient) {
  return useQuery({
    queryKey: queryKeys.imports,
    queryFn: async () =>
      importsPage.parse(
        unwrap(await client.GET("/imports", { params: { query: { limit: 100 } } })),
      ),
    refetchInterval: (query) =>
      query.state.data?.items.some((i) => i.status === "validating" || i.status === "committing")
        ? 1500
        : false,
  });
}

export function useQuarantine(client: ApiClient, importId: string | null) {
  return useQuery({
    queryKey: queryKeys.quarantine(importId ?? ""),
    queryFn: async () =>
      quarantinePage.parse(
        unwrap(
          await client.GET("/imports/{importId}/quarantine", {
            params: { path: { importId: importId! }, query: { limit: 200 } },
          }),
        ),
      ),
    enabled: importId !== null,
  });
}

export function useConnections(client: ApiClient) {
  return useQuery({
    queryKey: queryKeys.connections,
    queryFn: async () => connectionsResponse.parse(unwrap(await client.GET("/connections"))),
    staleTime: 15_000,
  });
}

export function useSyncs(client: ApiClient, connectionId: string | null) {
  return useQuery({
    queryKey: queryKeys.syncs(connectionId ?? ""),
    queryFn: async (): Promise<ConnectionSync[]> =>
      z.array(connectionSyncSchema).parse(
        unwrap(
          await client.GET("/connections/{connectionId}/syncs", {
            params: { path: { connectionId: connectionId! } },
          }),
        ),
      ),
    enabled: connectionId !== null,
    refetchInterval: (query) =>
      query.state.data?.some((s) => s.status === "running") ? 1500 : false,
  });
}

export interface UploadInput {
  entityKind: string;
  connectionId: string;
  mappingId?: string | undefined;
  file: File;
}

/** Upload is multipart, which the generated client does not shape; the fields go before the file (IMP-01). */
export function useUploadImport(client: ApiClient, getToken: () => Promise<string | null>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UploadInput): Promise<Import> => {
      const form = new FormData();
      form.set("entityKind", input.entityKind);
      form.set("connectionId", input.connectionId);
      if (input.mappingId) form.set("mappingId", input.mappingId);
      form.set("file", input.file, input.file.name);
      const token = await getToken();
      const response = await fetch(`${window.location.origin}/api/v1/imports`, {
        method: "POST",
        headers: token ? { authorization: `Bearer ${token}` } : {},
        body: form,
      });
      const body: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        const detail =
          body && typeof body === "object" && "detail" in body
            ? String(body.detail)
            : response.statusText;
        throw new Error(detail);
      }
      return importSchema.parse(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.imports });
      void queryClient.invalidateQueries({ queryKey: queryKeys.connections });
    },
  });
  void client;
}

export function useCommitImport(client: ApiClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (importId: string): Promise<Import> =>
      importSchema.parse(
        unwrap(await client.POST("/imports/{importId}/commit", { params: { path: { importId } } })),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.imports }),
  });
}

export function useTriggerSync(client: ApiClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (connectionId: string): Promise<ConnectionSync> =>
      connectionSyncSchema.parse(
        unwrap(
          await client.POST("/connections/{connectionId}/syncs", {
            params: { path: { connectionId } },
          }),
        ),
      ),
    onSuccess: (_sync, connectionId) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.syncs(connectionId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.connections });
      void queryClient.invalidateQueries({ queryKey: queryKeys.imports });
    },
  });
}

const supplyPointsPage = z.object({
  items: z.array(supplyPointSummarySchema),
  nextCursor: z.string().nullable(),
  total: z.number().int(),
});

export interface SupplyPointQuery {
  q?: string | undefined;
  readMethod?: "smart" | "amr" | "manual" | undefined;
  minConfidence?: number | undefined;
  maxConfidence?: number | undefined;
  sort?: "spid" | "confidence_asc" | "confidence_desc" | "last_read_asc" | undefined;
  cursor?: string | undefined;
  limit?: number | undefined;
}

export function useSupplyPoints(client: ApiClient, query: SupplyPointQuery) {
  const params = Object.fromEntries(
    Object.entries(query).filter(([, v]) => v !== undefined && v !== ""),
  );
  return useQuery({
    queryKey: queryKeys.supplyPoints(params),
    queryFn: async () =>
      supplyPointsPage.parse(
        unwrap(
          await client.GET("/supply-points", { params: { query: { limit: 100, ...params } } }),
        ),
      ),
    placeholderData: (previous) => previous,
  });
}

export function useSupplyPoint(client: ApiClient, spid: string) {
  return useQuery({
    queryKey: queryKeys.supplyPoint(spid),
    queryFn: async (): Promise<SupplyPointDetail> =>
      supplyPointDetailSchema.parse(
        unwrap(await client.GET("/supply-points/{spid}", { params: { path: { spid } } })),
      ),
    // A record that does not exist is an answer, not a transient failure.
    retry: (count, error) =>
      !(error instanceof ApiProblem && error.problem.status === 404) && count < 2,
  });
}

export function useTimeline(client: ApiClient, spid: string) {
  return useQuery({
    queryKey: queryKeys.timeline(spid),
    queryFn: async (): Promise<TimelineEntry[]> =>
      z.object({ items: z.array(timelineEntrySchema), nextCursor: z.string().nullable() }).parse(
        unwrap(
          await client.GET("/supply-points/{spid}/events", {
            params: { path: { spid }, query: { limit: 200 } },
          }),
        ),
      ).items,
  });
}

export function useReads(client: ApiClient, meterId: string | null, from: string) {
  return useQuery({
    queryKey: queryKeys.reads(meterId ?? ""),
    queryFn: async () =>
      z.array(meterReadSchema).parse(
        unwrap(
          await client.GET("/meters/{meterId}/reads", {
            params: { path: { meterId: meterId! }, query: { from } },
          }),
        ),
      ),
    enabled: meterId !== null,
  });
}

export type { Connection, Import, QuarantinedRow };

// --- findings (Phase 2) ------------------------------------------------------------

const findingsPage = z.object({
  items: z.array(findingSchema),
  nextCursor: z.string().nullable(),
  total: z.number().int(),
});

export interface FindingsQuery {
  view?: "mine" | "my_team" | "awaiting_party" | "overdue" | "all" | undefined;
  ruleId?: string | undefined;
  severity?: "critical" | "high" | "medium" | "low" | undefined;
  status?: Finding["status"] | undefined;
  spid?: string | undefined;
  sort?: "impact_desc" | "sla_asc" | "severity_desc" | "newest" | undefined;
  cursor?: string | undefined;
  limit?: number | undefined;
}

export function useFindings(client: ApiClient, query: FindingsQuery, enabled = true) {
  const params = Object.fromEntries(
    Object.entries(query).filter(([, v]) => v !== undefined && v !== ""),
  );
  return useQuery({
    queryKey: queryKeys.findings(params),
    queryFn: async () =>
      findingsPage.parse(unwrap(await client.GET("/findings", { params: { query: params } }))),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function useFinding(client: ApiClient, findingId: string | null) {
  return useQuery({
    queryKey: queryKeys.finding(findingId ?? ""),
    queryFn: async (): Promise<FindingDetail> =>
      findingDetailSchema.parse(
        unwrap(
          await client.GET("/findings/{findingId}", {
            params: { path: { findingId: findingId! } },
          }),
        ),
      ),
    enabled: findingId !== null,
    retry: (count, error) =>
      !(error instanceof ApiProblem && error.problem.status === 404) && count < 2,
  });
}

export interface TransitionInput {
  findingId: string;
  to: Finding["status"];
  reason?: string | undefined;
  reasonCode?: string | undefined;
  assigneeUserId?: string | undefined;
  awaitingRef?: string | undefined;
}

function invalidateFindings(queryClient: ReturnType<typeof useQueryClient>, findingId: string) {
  void queryClient.invalidateQueries({ queryKey: ["findings"] });
  void queryClient.invalidateQueries({ queryKey: queryKeys.finding(findingId) });
  void queryClient.invalidateQueries({ queryKey: ["supply-points"] });
  void queryClient.invalidateQueries({ queryKey: queryKeys.modules });
}

/** A move through the state machine (FND-02); a refused move carries the API's reason. */
export function useTransitionFinding(client: ApiClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: TransitionInput): Promise<Finding> => {
      const { findingId, ...rest } = input;
      const body = Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== undefined)) as {
        to: Finding["status"];
      } & Record<string, string>;
      return findingSchema.parse(
        unwrap(
          await client.POST("/findings/{findingId}/transitions", {
            params: { path: { findingId } },
            body,
          }),
        ),
      );
    },
    onSuccess: (_f, input) => invalidateFindings(queryClient, input.findingId),
  });
}

/** Held evidence or a cited read, linked to the finding (D-42). */
export function useLinkEvidence(client: ApiClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      findingId: string;
      evidenceId?: string | undefined;
      readId?: string | undefined;
    }): Promise<FindingDetail> =>
      findingDetailSchema.parse(
        unwrap(
          await client.POST("/findings/{findingId}/evidence", {
            params: { path: { findingId: input.findingId } },
            body: input.evidenceId
              ? { evidenceId: input.evidenceId }
              : { readId: input.readId ?? "" },
          }),
        ),
      ),
    onSuccess: (_d, input) => invalidateFindings(queryClient, input.findingId),
  });
}

// --- rules and runs (Phase 2) ----------------------------------------------------

export function useRules(client: ApiClient) {
  return useQuery({
    queryKey: queryKeys.rules,
    queryFn: async (): Promise<Rule[]> =>
      z.array(ruleSchema).parse(unwrap(await client.GET("/rules"))),
    staleTime: 15_000,
  });
}

export interface RuleChange {
  ruleId: string;
  enabled?: boolean | undefined;
  severity?: Rule["severity"] | undefined;
  params?: Record<string, unknown> | undefined;
  reason: string;
}

/** A threshold, severity or enablement change with a reason (RUL-03); applies from the next run. */
export function useUpdateRule(client: ApiClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (change: RuleChange): Promise<Rule> => {
      const { ruleId, ...rest } = change;
      const body = Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== undefined)) as {
        reason: string;
      } & Record<string, unknown>;
      return ruleSchema.parse(
        unwrap(await client.PATCH("/rules/{ruleId}", { params: { path: { ruleId } }, body })),
      );
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.rules }),
  });
}

/** The runs, newest first; refreshed while one is running. */
export function useRuleRuns(client: ApiClient) {
  return useQuery({
    queryKey: queryKeys.ruleRuns,
    queryFn: async (): Promise<RuleRun[]> =>
      z.array(ruleRunSchema).parse(unwrap(await client.GET("/rule-runs"))),
    refetchInterval: (query) =>
      query.state.data?.some((r) => r.status === "running") ? 1500 : false,
  });
}

export function useStartRun(client: ApiClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<RuleRun> =>
      ruleRunSchema.parse(unwrap(await client.POST("/rule-runs", { body: {} }))),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.ruleRuns }),
  });
}

// --- field jobs and evidence (Phase 3) ---------------------------------------------

export const partySchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.enum(["wholesaler", "retailer", "self_supplier", "contractor"]),
  isSynthetic: z.boolean().optional(),
});
export type Party = z.infer<typeof partySchema>;

export interface FieldJobsQuery {
  status?: FieldJob["status"] | undefined;
  type?: FieldJob["type"] | undefined;
  findingId?: string | undefined;
  supplyPointId?: string | undefined;
  cursor?: string | undefined;
  limit?: number | undefined;
}

function invalidateField(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ["field-jobs"] });
  void queryClient.invalidateQueries({ queryKey: ["findings"] });
  void queryClient.invalidateQueries({ queryKey: ["supply-points"] });
  void queryClient.invalidateQueries({ queryKey: queryKeys.ruleRuns });
  void queryClient.invalidateQueries({ queryKey: queryKeys.modules });
}

export function useFieldJobs(client: ApiClient, query: FieldJobsQuery, enabled = true) {
  const params = Object.fromEntries(
    Object.entries(query).filter(([, v]) => v !== undefined && v !== ""),
  );
  return useQuery({
    queryKey: ["field-jobs", params],
    queryFn: async (): Promise<FieldJobPage> =>
      fieldJobPageSchema.parse(
        unwrap(await client.GET("/field-jobs", { params: { query: params } })),
      ),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function useFieldJob(client: ApiClient, jobId: string | null) {
  return useQuery({
    queryKey: ["field-jobs", "one", jobId ?? ""],
    queryFn: async (): Promise<FieldJob> =>
      fieldJobSchema.parse(
        unwrap(await client.GET("/field-jobs/{jobId}", { params: { path: { jobId: jobId! } } })),
      ),
    enabled: jobId !== null,
    retry: false,
  });
}

/** Contractors and the tenant's own party, for the assignee of a job (D-59). */
export function useParties(client: ApiClient) {
  return useQuery({
    queryKey: ["parties"],
    queryFn: async (): Promise<Party[]> =>
      z.array(partySchema).parse(unwrap(await client.GET("/admin/parties"))),
    staleTime: 5 * 60_000,
  });
}

export interface RaiseInput {
  type?: FieldJob["type"] | undefined;
  supplyPointId?: string | undefined;
  findingId?: string | undefined;
  assigneePartyId?: string | undefined;
}

export function useRaiseFieldJob(client: ApiClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: RaiseInput): Promise<FieldJob> => {
      const body = Object.fromEntries(
        Object.entries(input).filter(([, v]) => v !== undefined && v !== ""),
      ) as {
        type?: FieldJob["type"];
        supplyPointId?: string;
        findingId?: string;
        assigneePartyId?: string;
      };
      return fieldJobSchema.parse(unwrap(await client.POST("/field-jobs", { body })));
    },
    onSuccess: () => invalidateField(queryClient),
  });
}

export interface CompleteInput {
  jobId: string;
  outcome: "completed" | "aborted";
  abortReason?: "no_access" | "unsafe" | "not_found" | "other" | undefined;
  result?: FieldJobResult | undefined;
  evidenceIds: string[];
}

export function useCompleteFieldJob(client: ApiClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CompleteInput): Promise<FieldJob> => {
      const { jobId, ...rest } = input;
      const body = Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== undefined)) as {
        outcome: CompleteInput["outcome"];
        abortReason?: NonNullable<CompleteInput["abortReason"]>;
        // The wire type: the parsed result's optional members never carry undefined once JSON-encoded.
        result?: components["schemas"]["FieldJobResult"];
        evidenceIds: string[];
      };
      return fieldJobSchema.parse(
        unwrap(
          await client.POST("/field-jobs/{jobId}/complete", { params: { path: { jobId } }, body }),
        ),
      );
    },
    onSuccess: () => invalidateField(queryClient),
  });
}

/** Records the exchange a completed job holds but the registry never heard about (D-61). */
export function useRecordExchange(client: ApiClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string): Promise<FieldJob> =>
      fieldJobSchema.parse(
        unwrap(
          await client.POST("/field-jobs/{jobId}/record-exchange", { params: { path: { jobId } } }),
        ),
      ),
    onSuccess: () => invalidateField(queryClient),
  });
}

async function problemDetail(response: Response): Promise<string> {
  const body: unknown = await response.json().catch(() => null);
  return body && typeof body === "object" && "detail" in body
    ? String(body.detail)
    : response.statusText;
}

/** A photo, document or correspondence, scanned and stored before it is recorded (D-54). */
export function useUploadEvidence(getToken: () => Promise<string | null>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      file: File;
      kind: "photo" | "document" | "correspondence";
      attachTo: { type: "finding" | "field_job" | "supply_point" | "meter"; id: string }[];
    }): Promise<Evidence> => {
      const form = new FormData();
      form.set("kind", input.kind);
      form.set("attachTo", JSON.stringify(input.attachTo));
      form.set("file", input.file, input.file.name);
      const token = await getToken();
      const response = await fetch(`${window.location.origin}/api/v1/evidence`, {
        method: "POST",
        headers: token ? { authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (!response.ok) throw new Error(await problemDetail(response));
      return evidenceSchema.parse(await response.json());
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["findings"] }),
  });
}

/**
 * Exports jobs as CSV or the job object and hands the file to the browser; the
 * jobs are dispatched (FLD-02, D-63). Answers how many went out.
 */
export async function exportFieldJobs(
  getToken: () => Promise<string | null>,
  jobIds: string[],
  format: "csv" | "job_object",
): Promise<{ dispatched: number; fileName: string }> {
  const token = await getToken();
  const response = await fetch(`${window.location.origin}/api/v1/field-jobs/export`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ jobIds, format }),
  });
  if (!response.ok) throw new Error(await problemDetail(response));
  const disposition = response.headers.get("content-disposition") ?? "";
  const fileName =
    /filename="([^"]+)"/.exec(disposition)?.[1] ??
    (format === "csv" ? "field-jobs.csv" : "field-jobs.json");
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return {
    dispatched: Number(response.headers.get("x-jobs-dispatched") ?? jobIds.length),
    fileName,
  };
}
